import { CurrencyPipe, DatePipe, Location, NgClass, NgOptimizedImage, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { GiftCartItem, SpaBooking, StandardCartItem } from '../../../../../core/model/cart/cart-item.model';
import { SignInComponent } from '../../account/sign-in/sign-in.component';
import { emulateClick } from '../../core/a11y';
import { DrawerService } from '../../drawer/drawer.service';
import { GalleryComponent } from '../../gallery/gallery.component';
import { NotificationService } from '../../notification/notification.service';
import { ShareSocialsComponent } from '../../share-socials/share-socials.component';
import { InnerHtmlComponent } from '../../shared/components/inner-html/inner-html.component';
import { SpaBookingComponent } from '../../shared/components/spa-booking/spa-booking.component';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientGreenDirective } from '../../shared/directives/hover-gradient-green.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { ProductGroup } from '../../shared/model/product-group.model';
import { Product } from '../../shared/model/product.model';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { AuthService } from '../../shared/services/auth.service';
import { CartService } from '../../shared/services/cart.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { ProductService } from '../../shared/services/product.service';
import { SiteService } from '../../shared/services/site.service';
import { ProductsComponent } from '../products.component';
import { Gender } from '../../../../../core/profile/gender';
import { ProductType } from '../../../../../core/model/product.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-product',
  imports: [
    NgOptimizedImage,
    AutoCloseDirective,
    NgxTolgeeModule,
    HoverGradientDirective,
    DatePipe,
    CurrencyPipe,
    NgClass,
    ProgressClickDirective,
    FormsModule,
    ProductsComponent,
    RouterLink,
    HoverGradientGreenDirective,
    SpaBookingComponent,
    InnerHtmlComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './product.component.html'
})
export class ProductComponent implements OnInit, OnDestroy {
  document = inject(DOCUMENT);
  viewportScroller = inject(ViewportScroller);
  route = inject(ActivatedRoute);
  router = inject(Router);
  location = inject(Location);
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  cartService = inject(CartService);
  siteService = inject(SiteService);
  analyticsService = inject(AnalyticsService);
  drawerService = inject(DrawerService);
  navigationService = inject(NavigationService);
  productService = inject(ProductService);
  center = this.siteService.center;
  product: WritableSignal<Product | ProductGroup> = signal(new ProductGroup());
  addedToCard = signal(false);
  planningDisplayed = signal(false);
  products = computed(() => {
    return (this.product() as ProductGroup).products;
  });
  isGroup = false;
  selectedProduct = signal(new Product());
  tab = signal<'goodToKnow' | 'bePrepared' | 'access'>('goodToKnow');
  giftMode = signal(false);
  quantity = signal(1);
  price = computed(() => this.siteService.price(this.selectedProduct().id));
  images = computed(() => this.siteService.images(this.product().id))
  total = computed(() => {
    const price = this.price();
    return price.price * this.quantity();
  });
  taxes = computed(() => (this.total() * 10 / 12).toFixed(2));
  selectedSpaBooking = signal<SpaBooking | null>(null);
  isRituel = computed(() => {
    const spaBooking = this.selectedSpaBooking();
    if (!spaBooking) {
      return false
    }
    return spaBooking.products.length > 1 && spaBooking.products[0].erpId === 106
  })
  protected readonly emulateClick = emulateClick;
  protected readonly GalleryComponent = GalleryComponent;
  protected readonly SignInComponent = SignInComponent;
  protected readonly ShareSocialsComponent = ShareSocialsComponent;
  protected readonly Gender = Gender;
  protected readonly ProductType = ProductType;

  constructor() {
    effect(() => {
      if (this.selectedSpaBooking()) {
        this.viewportScroller.scrollToAnchor('cartCard')
      }
    });
    effect(() => {
      const selected = this.selectedProduct();
      const navigationEnded = this.navigationService.navigationEnded();
      if (!navigationEnded) {
        return;
      }
      const url = this.router.createUrlTree(['..', selected.slug], {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      }).toString()
      this.location.replaceState(url);
      this.analyticsService.viewItem(selected);
    });
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe(map => {
        this.giftMode.set(map.get('gift') === 'true');
      });
    this.route.data
      .pipe(takeUntilDestroyed())
      .subscribe(({ product }) => {
        this.product.set(product);
        this.isGroup = product instanceof ProductGroup;
        if (this.isGroup) {
          this.selectedProduct.set((product as ProductGroup).products
            .find(p => p.slug === this.route.snapshot.paramMap.get('product')) ?? product.products[0]);
        } else {
          this.selectedProduct.set(product)
        }
        this.addedToCard.set(false);
        this.quantity.set(1);
      });
  }

  ngOnInit(): void {
    this.document.getElementById('axeptio_main_button')?.classList.add('product')
  }

  ngOnDestroy(): void {
    this.document.getElementById('axeptio_main_button')?.classList.remove('product')
  }

  addToCart = async (): Promise<void> => {
    await this.cartService.add([{
      cartItem: this.giftMode() ?
        new GiftCartItem({ product: this.selectedProduct().id }) :
        new StandardCartItem({ product: this.selectedProduct().id, spaBooking: this.selectedSpaBooking() }),
      quantity: this.quantity()
    }]);
    this.addedToCard.set(true);
    this.quantity.set(1);
    if (this.selectedSpaBooking()) {
      this.cartService.refreshCart();
    }
  }

  updateQuantity(delta: -1 | 1): void {
    this.quantity.update(qty => Math.max(1, qty + delta))
  }

  setQuantity(qty: string | number): void {
    this.quantity.set(Number(qty));
  }

  /**
   * Open login dialog if user is not connected else displays the SPA planning
   */
  displayPlanning(): void {
    this.planningDisplayed.set(true);
  }

  /**
   * Open login dialog if user is not connected else displays the SPA planning
   */
  hidePlanning(): void {
    this.planningDisplayed.set(false);
  }

  async validatePlanning($event: SpaBooking): Promise<void> {
    this.selectedSpaBooking.set($event)
    this.planningDisplayed.set(false)
    this.quantity.set(1)
    await this.addToCart();
    await this.cartService.refreshCart()
  }
}
